import { HTMLAttributes, ReactElement, useCallback, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { confirmSignIn } from "aws-amplify/auth";
import { match } from "ts-pattern";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useBoolean } from "@/hooks/use-boolean";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { PinInput, PinInputField } from "@/components/pin-input";
import { StatusButton } from "@/components/ui/loading-button";

type OtpFormProps = HTMLAttributes<HTMLDivElement> & {
  isSuperadmin?: boolean;
};

const formSchema = z.object({
  otp: z.string().min(1, { message: "Please enter your otp code." }),
});

type FormData = z.infer<typeof formSchema>;

const PIN_LENGTH = 6;
const SEPARATOR_INDEX = 3;

export function OtpForm({
  className,
  isSuperadmin = false,
  ...props
}: Readonly<OtpFormProps>) {
  const navigate = useNavigate();
  const [disabledBtn, { setTrue: disableBtn, setFalse: enableBtn }] =
    useBoolean(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { otp: "" },
  });

  const onSubmit = async (data: FormData) => {
    try {
      setIsSubmitting(true);
      const { nextStep } = await confirmSignIn({ challengeResponse: data.otp });

      void match(nextStep.signInStep)
        .with("DONE", async () => {
          void navigate({
            to: isSuperadmin ? "/root-user/dashboard" : "/dashboard",
          });
        })
        .otherwise(() => {});
    } catch (error) {
      if (error instanceof Error) {
        console.log(error.message);
        toast.error(error.message || "Invalid OTP code. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderPinFields = () => {
    const fields = Array.from({ length: PIN_LENGTH + 1 }).map((_, i) =>
      i === SEPARATOR_INDEX ? (
        <Separator key={i} orientation="vertical" />
      ) : (
        <PinInputField
          key={i}
          component={Input}
          className={cn(form.getFieldState("otp").invalid && "border-red-500")}
        />
      ),
    );
    return fields as ReactElement<typeof PinInputField>[];
  };

  return (
    <div className={cn("grid gap-6", className)} {...props}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid gap-2">
            <FormField
              control={form.control}
              name="otp"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormControl>
                    <PinInput
                      {...field}
                      className="flex h-10 justify-between"
                      onComplete={enableBtn}
                      onIncomplete={disableBtn}
                    >
                      {renderPinFields()}
                    </PinInput>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <StatusButton
              type="submit"
              className="mt-2"
              isLoading={isSubmitting}
              disabled={disabledBtn}
            >
              Verify
            </StatusButton>
          </div>
        </form>
      </Form>
    </div>
  );
}
